// apps/client/src/pages/ClientSetsPage.tsx
import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { UsersRound } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Spinner } from '../components/common/Spinner';
import { useClientSets } from '../hooks/useClientSets';
import { useDeleteClientSet } from '../hooks/useDeleteClientSet';
import { CreateClientSetDialog } from '../components/clientSets/CreateClientSetDialog';
import { ClientSetDetailDialog } from '../components/clientSets/ClientSetDetailDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import type { ClientSet } from '../hooks/useClientSets';

const ClientSetsPage: React.FC = () => {
  const { userData } = useOutletContext<any>();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedClientSet, setSelectedClientSet] = useState<ClientSet | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { clientSets, loading, error, refetch } = useClientSets({
    companyId: userData?.companyId || '',
    userId: userData?.uid,
    userRole: userData?.role || 'broker',
  });

  const { deleteClientSet } = useDeleteClientSet();

  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    refetch();
  };

  const handleViewDetails = (clientSet: ClientSet) => {
    setSelectedClientSet(clientSet);
  };

  const handleDeleteClick = (e: React.MouseEvent, clientSetId: string) => {
    e.stopPropagation(); // Prevent card click
    setDeleteConfirmId(clientSetId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setDeletingId(deleteConfirmId);
    setDeleteConfirmId(null);

    const success = await deleteClientSet(deleteConfirmId);
    setDeletingId(null);

    if (success) {
      refetch();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={UsersRound}
        title="Client Groups"
        subtitle="Group related customer names to view unified portfolios"
        actions={
          <button
            onClick={() => setShowCreateDialog(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Client Group
          </button>
        }
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Client Sets Grid */}
      {!loading && !error && (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {clientSets.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 text-lg mb-4">No client groups yet</div>
              <p className="text-gray-500 mb-6">
                Create your first client group to track unified customer portfolios
              </p>
              <button
                onClick={() => setShowCreateDialog(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create First Client Group
              </button>
            </div>
          ) : (
            clientSets.map((clientSet) => (
              <div
                key={clientSet.id}
                className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer relative"
                onClick={() => handleViewDetails(clientSet)}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-white">
                    {clientSet.clientSetName}
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleDeleteClick(e, clientSet.id)}
                      disabled={deletingId === clientSet.id}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                      title="Delete client group"
                    >
                      {deletingId === clientSet.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                    <svg
                      className="w-6 h-6 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-400">
                  <div className="flex justify-between">
                    <span>Customer Names:</span>
                    <span className="text-white font-medium">
                      {clientSet.customerNames.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Loans:</span>
                    <span className="text-white font-medium">
                      {clientSet.loanCount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Value:</span>
                    <span className="text-white font-medium">
                      ${(clientSet.totalValueCents / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-700 text-xs text-gray-500">
                  Updated {new Date(clientSet.updatedAt).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Dialog */}
      {showCreateDialog && (
        <CreateClientSetDialog
          onClose={() => setShowCreateDialog(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Detail Dialog */}
      {selectedClientSet && (
        <ClientSetDetailDialog
          clientSetId={selectedClientSet.id}
          clientSetName={selectedClientSet.clientSetName}
          onClose={() => setSelectedClientSet(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmId !== null}
        title="Delete Client Group"
        message="Are you sure you want to delete this client group? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default ClientSetsPage;
